import { IconCircleCheckFilled } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import ClassicLoader from "@/components/common/classic-loader";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getProductReviews } from "@/features/rating/api/get-product-reviews";
import { useProductReviews } from "@/features/rating/api/hooks/use-product-reviews";
import { RatingCategoryAverages } from "@/features/rating/components/rating-category-averages";
import { RatingFilters } from "@/features/rating/components/rating-filters";
import { ReviewItem } from "@/features/rating/components/review-item";
import type {
  ProductRating,
  ProductReviewsSort,
} from "@/features/rating/types/product-rating.type";
import { useMobile } from "@/lib/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { StarRating } from "./star-rating";

export const RatingDetails = ({
  rating,
  productId,
  merchantName,
}: {
  rating: ProductRating;
  productId: string;
  merchantName?: string;
}) => {
  const t = useTranslations("rating");
  const { isMobile } = useMobile();

  const [sort, setSort] = useState<ProductReviewsSort>("desc");
  const [ratingFilter, setRatingFilter] = useState<number>();
  const [withText, setWithText] = useState(false);

  const filters = useMemo(
    () => ({ sort, rating: ratingFilter, withText }),
    [sort, ratingFilter, withText],
  );

  const {
    data,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useProductReviews({ productId, filters, enabled: true });

  const { data: textReviewsCount } = useQuery({
    queryKey: ["product-reviews-text-count", productId],
    queryFn: () =>
      getProductReviews(productId, 1, 1, { sort: "desc", withText: true }),
    select: (response) => response.pagination.total,
    retry: false,
  });

  const reviews = data?.pages.flatMap((page) => page.reviews) ?? [];
  const hasActiveFilters = ratingFilter !== undefined || withText;

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!(sentinel && hasNextPage)) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) {
        fetchNextPage();
      }
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const resetFilters = () => {
    setRatingFilter(undefined);
    setWithText(false);
  };
  const reviewsList = (
    <div className="flex flex-col gap-y-4">
      {isPending && (
        <div className="flex h-16 items-center justify-center">
          <ClassicLoader />
        </div>
      )}

      {isError && !isPending && (
        <p className="text-brand-green2">{t("error")}</p>
      )}

      {!(isPending || isError) && reviews.length === 0 && (
        <div className="flex flex-col items-center gap-y-3 py-8 text-center">
          <p className="font-medium text-xl">{t("noFilteredReviews")}</p>
          <p className="text-brand-green2">
            {t("noFilteredReviewsDescription")}
          </p>
          {hasActiveFilters && (
            <Button
              onClick={resetFilters}
              variant="outline"
              size="wide"
              className="bg-transparent max-md:hidden"
            >
              {t("showAllTextReviews")}
            </Button>
          )}
        </div>
      )}

      {reviews.map((review) => (
        <ReviewItem
          key={review.id}
          merchantName={merchantName}
          review={review}
        />
      ))}

      {isFetchingNextPage && (
        <div className="flex h-12 items-center justify-center">
          <ClassicLoader />
        </div>
      )}

      <div ref={sentinelRef} />
    </div>
  );

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col gap-y-4",
        reviews.length === 0 && "rounded-b-lg bg-brand-grey1",
      )}
    >
      <div className="rounded-lg bg-brand-grey1">
        <div className="flex w-full items-stretch gap-4 p-4 sm:gap-6 md:p-6">
          <div className="flex shrink-0 flex-col items-start gap-y-2">
            <p className="flex items-baseline gap-x-1">
              <span className="font-bold text-2xl tabular-nums md:text-3.5xl">
                {rating.average.toFixed(1)}
              </span>
              <span className="text-muted-foreground">/ 5</span>
            </p>
            <StarRating
              rating={rating.average}
              starClassName="h-4 w-4 md:h-[21px] md:w-[21px]"
              containerClassName="gap-1.5"
            />
            <p className="text-muted-foreground max-md:text-sm">
              {t("reviewsCount", { count: rating.count })}
              {textReviewsCount ? (
                <> {t("commentsCount", { count: textReviewsCount })}</>
              ) : null}
            </p>
          </div>
          <div className="flex-1 border-border border-l pl-4 md:pl-6">
            <RatingCategoryAverages averages={rating.ratingDetailsAverages} />
          </div>
        </div>

        <div className="flex items-center gap-x-1.5 bg-brand-green-light/45 p-4 text-brand-green1 max-md:py-2.5 max-md:text-xs">
          <IconCircleCheckFilled className="h-4.5 w-4.5 shrink-0 text-brand-green md:h-6 md:w-6" />
          {t("purchaseConfirmed")}
        </div>
        <RatingFilters
          onRatingChange={setRatingFilter}
          onSortChange={setSort}
          onWithTextChange={setWithText}
          rating={ratingFilter}
          sort={sort}
          withText={withText}
        />
      </div>

      {isMobile ? (
        reviewsList
      ) : (
        <ScrollArea className="min-h-0 w-full flex-1">{reviewsList}</ScrollArea>
      )}
    </div>
  );
};
